import type { TableOrder, TableReservation, VendorShow } from "@hobbyshopify/backend-client";
import type { Stripe } from "@stripe/stripe-js";
import { Rx, RxList } from "subtree.js/core";
import { type FormStatus, idleForm, idleLoad, type LoadStatus } from "../../../../core/primitives/load-status";

/** The payment open for the table the shop holds at a show. */
export interface OpenTablePayment {
  readonly eventId: string;
  readonly order: TableOrder;
  readonly clientSecret: string;
  /** Stripe.js for the host's account; resolves null where the app has no publishable key. */
  readonly stripe: Promise<Stripe | null>;
}

/** `paying` while Stripe confirms the card; `confirming` once it has, until the table reads as the shop's. */
export type PaymentPhase = "open" | "paying" | "confirming";

/** Other shops' shows the shop may sell at, and the table it takes or pays for at one. */
export class VendorShowsSectionState {
  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly shows = new RxList<VendorShow>();
  /** Tables bought on the storefront for the member's verified email, no shop's yet. */
  readonly reservations = new RxList<TableReservation>();
  /** The reservation code being typed. */
  readonly code = new Rx("");
  /** Whether the signed-in member may take a table: a Manager or an Owner. */
  readonly mayTake = new Rx(false);
  /**
   * The kind being taken, the order whose payment is being opened, or the show whose table is being
   * given up; and how it went.
   */
  readonly busyId = new Rx<string | null>(null);
  readonly action = new Rx<FormStatus>(idleForm);
  readonly payment = new Rx<OpenTablePayment | null>(null);
  readonly paymentPhase = new Rx<PaymentPhase>("open");
  /** Orders Stripe has the money for, until the server says so: offered neither for paying nor for giving up. */
  readonly confirmed = new Rx<ReadonlySet<string>>(new Set());
}

export abstract class VendorShowsSectionActions {
  abstract reload(): Promise<void>;
  /** Holds a table of the kind for five minutes and opens its payment. */
  abstract take(eventId: string, kindId: string): Promise<void>;
  /** Opens the payment for the table held at the show again. */
  abstract pay(eventId: string): Promise<void>;
  /** The card is about to go to Stripe: false while the section is busy with something else, and nothing is sent. */
  abstract paymentStarted(): boolean;
  /** Stripe refused the payment, in its own words; the shop may try again while the hold lasts. */
  abstract paymentFailed(message: string): void;
  /** Stripe took the payment: the table is the shop's once the server hears so. */
  abstract paymentConfirmed(): Promise<void>;
  /** Gives up the table held at the show. */
  abstract cancel(eventId: string): Promise<void>;
  abstract setCode(code: string): void;
  /** Puts a reserved table on the shop: the code typed, or a reservation's own. */
  abstract redeem(code?: string): Promise<void>;
}
